import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close, Save, Add, Person } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { createAdmin, updateAdmin } from '../../store/slices/adminsSlice';
import { AdminData, AdminForm, AdminUpdateForm } from '../../types';
import { toast } from 'react-toastify';

interface AdminFormModalProps {
  open: boolean;
  onClose: () => void;
  admin?: AdminData | null;
  mode: 'create' | 'edit';
}

const AdminFormModal: React.FC<AdminFormModalProps> = ({
  open,
  onClose,
  admin,
  mode,
}) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state: RootState) => state.admins);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canAssignElevatedRoles =
    currentUser?.role === 'owner' || currentUser?.role === 'superadmin';

  const [formData, setFormData] = useState<AdminForm>({
    fullName: '',
    email: '',
    phoneNumber: '',
    role: 'admin',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (admin && mode === 'edit') {
      setFormData({
        fullName: admin.fullName,
        email: admin.email,
        phoneNumber: admin.phoneNumber,
        role: admin.role,
      });
    } else {
      setFormData({
        fullName: '',
        email: '',
        phoneNumber: '',
        role: 'admin',
      });
    }
    setErrors({});
  }, [admin, mode, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (mode === 'create') {
        const result = await dispatch(createAdmin(formData) as any);
        if (createAdmin.rejected.match(result)) {
          const msg =
            result.payload ||
            result.error?.message ||
            'Failed to create admin';
          toast.error(typeof msg === 'string' ? msg : 'Failed to create admin');
          return;
        }
        toast.success('Admin created successfully');
      } else if (admin) {
        const updateData: AdminUpdateForm = {
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,
          role: formData.role,
        };
        const result = await dispatch(updateAdmin({ adminId: admin.id, adminData: updateData }) as any);
        if (updateAdmin.rejected.match(result)) {
          toast.error(result.error?.message || 'Failed to update admin');
          return;
        }
        toast.success('Admin updated successfully');
      }
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || 'Failed to save admin');
    }
  };

  const handleInputChange = (field: keyof AdminForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    ...(canAssignElevatedRoles
      ? [
          { value: 'superadmin', label: 'Super Admin' },
          { value: 'owner', label: 'Owner' },
        ]
      : []),
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2 },
          maxHeight: { xs: 'calc(100dvh - 16px)', sm: '90vh' },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
     >
      <DialogTitle sx={{ 
        padding: { xs: 2, sm: 3 },
        paddingBottom: { xs: 1, sm: 2 }
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            {mode === 'create' ? 'Create Admin' : 'Edit Admin'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0, 
        padding: { xs: 2, sm: 3 },
        '&.MuiDialogContent-root': {
          paddingTop: { xs: 1, sm: 2 },
        }
      }}>
        <Box sx={{ mt: { xs: 0, sm: 2 } }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: 2, 
            mb: 2 
          }}>
            <TextField
              label="Full Name"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              error={!!errors.fullName}
              helperText={errors.fullName}
              fullWidth
              required
            />
            
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              required
              disabled={mode === 'edit'} // Email cannot be changed in edit mode
            />
          </Box>

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: 2, 
            mb: 2 
          }}>
            <TextField
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              fullWidth
              required
              placeholder="+1234567890"
            />

            <FormControl fullWidth error={!!errors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                label="Role"
              >
                {roleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person />
                      <span>{option.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Please fix the errors above before submitting.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        flexDirection: { xs: 'column', sm: 'row' }, 
        gap: { xs: 1, sm: 0 },
        padding: { xs: 2, sm: 1 },
        '& > *': {
          width: { xs: '100%', sm: 'auto' },
        }
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : mode === 'create' ? <Add /> : <Save />}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminFormModal; 